import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Info from '@mui/icons-material/Info';
import PrintIcon from '@mui/icons-material/Print';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ReactDOM from 'react-dom';
import BillPrint from '../stocktransfer/Bill';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import '../shared.css';

function StockTransferIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const history = useHistory();
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchtableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const fetchtableData = async () => {
        setData([]);
        try {
            const response = await fetchData('StockTransferApi', `/StockTransferoutDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') setData(response.response_data);
        } catch (error) {
            console.error('Error fetching Stock Transfer Details:', error);
            if (error.response?.status === 401) { logout(); history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`); }
        }
    };

    const filtered = data.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleDelete = () => {
        deleteData('StockTransferApi', `${deleteItemId}`, token)
            .then(() => { fetchtableData(); setDeleteItemId(null); setConfirmDialogOpen(false); })
            .catch(err => console.error(err));
    };

    const handleActivate = (id) => {
        activateData('StockTransferApi', `${id}`, token).then(fetchtableData).catch(console.error);
    };

    const handlePrint = async (id) => {
        try {
            const response = await fetchData('StockTransferApi', `/StockTransferPrint/${id}/${branchId}`, token);
            const labelData = response.response_data;
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);
            const doc = iframe.contentDocument || iframe.contentWindow.document;
            doc.write('<html><head><style>@media print{header,footer{display:none!important}}</style></head><body>');
            ReactDOM.render(<BillPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />, doc.body);
            doc.write('</body></html>');
            doc.close();
            setTimeout(() => iframe.contentWindow.print(), 1000);
        } catch (error) { console.error('Error printing:', error); }
    };

    function formatDate(d) { return new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit' }); }

    const rowBg = (item) => {
        if (!item.IsActive) return '#f5f5f5';
        if (item.InputRejected) return '#fef2f2';
        if (item.InputAccepted) return '#f0fdf4';
        return '#f5f0ff';
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Stock Transfer Out</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchtableData}><RefreshIcon /> Refresh</button>
                    </div>
                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}><SearchIcon /> Search</button>
                        {hasCreateMasterPermission && (
                            <Link to="/StockTransferCreate" className="pg-btn pg-btn-primary"><AddIcon /> Create New</Link>
                        )}
                    </div>
                    {showSearch && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>#</th><th>Bill Date</th><th>Bill Number</th><th>To Branch</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0
                            ? <tr><td colSpan={7} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filtered.map((item, i) => (
                                <tr key={item.Id} style={{ background: rowBg(item), opacity: item.IsActive ? 1 : 0.6 }}>
                                    <td>{i + 1}</td>
                                    <td>{formatDate(item.BillDate)}</td>
                                    <td>{item.BillPrefix}/S/{item.BillNumber}</td>
                                    <td>{item.ToBranchName}</td>
                                    <td style={{ textAlign: 'right' }}>{item.GrandTotal != null ? item.GrandTotal.toFixed(2) : '0.00'}</td>
                                    <td style={{ textAlign: 'right' }}>{item.TotalQuantity}</td>
                                    <td className="actions-cell">
                                        <Link to={`/StockTransferDetails/${item.Id}`}>
                                            <IconButton size="small" title="Details"><Info style={{ color: 'coral', fontSize: 17 }} /></IconButton>
                                        </Link>
                                        {item.InputRejected
                                            ? <span style={{ color: '#dc2626', fontSize: 11, fontWeight: 700, marginLeft: 4 }}>Rejected</span>
                                            : <>
                                                <IconButton size="small" onClick={() => handlePrint(item.Id)} title="Print">
                                                    <PrintIcon style={{ color: 'darkgoldenrod', fontSize: 17 }} />
                                                </IconButton>
                                                {hasEditMasterPermission && item.IsActive && (
                                                    <Link to={`/StockTransferEdit/${item.Id}`}>
                                                        <IconButton size="small" color="primary" title="Edit"><EditIcon style={{ fontSize: 17 }} /></IconButton>
                                                    </Link>
                                                )}
                                                {hasDeleteMasterPermission && (
                                                    <IconButton size="small"
                                                        onClick={() => item.IsActive ? (setDeleteItemId(item.Id), setConfirmDialogOpen(true)) : handleActivate(item.Id)}>
                                                        {item.IsActive
                                                            ? <DeleteIcon style={{ fontSize: 17, color: '#dc2626' }} />
                                                            : <ActivateIcon style={{ fontSize: 17, color: 'darkolivegreen' }} />}
                                                    </IconButton>
                                                )}
                                            </>
                                        }
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>

            <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
                <DialogTitle>Delete Record?</DialogTitle>
                <DialogContent><DialogContentText>Are you sure you want to delete this record?</DialogContentText></DialogContent>
                <DialogActions>
                    <button className="pg-btn pg-btn-danger" onClick={() => setConfirmDialogOpen(false)}>Cancel</button>
                    <button className="pg-btn pg-btn-success" onClick={handleDelete}>Confirm</button>
                </DialogActions>
            </Dialog>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default StockTransferIndex;
